import { useEffect, useRef } from "react";
import type { EngineError } from "../../domain/types";
import "./ErrorSummary.css";

export interface ErrorSummaryProps {
  error: EngineError;
  onRetry?: () => void;
}

const ERROR_COPY: Record<EngineError["code"], string> = {
  INPUT_INVALID:
    "No pudimos procesar esta entrada. Tus datos no se enviaron; corrige los campos señalados.",
  LIMIT_EXCEEDED: "La entrada supera un límite de tamaño o complejidad. Redúcela e inténtalo de nuevo.",
  RUN_CANCELLED: "Cancelaste la ejecución. Puedes reiniciarla cuando quieras.",
  DEPENDENCY_UNAVAILABLE:
    "El adaptador externo no respondió. Puedes continuar en modo determinista, que no depende de terceros.",
  INTERNAL_ERROR: "Ocurrió un fallo no clasificado. Guarda el identificador de correlación si vas a reportarlo.",
};

/**
 * Resumen de error enfocable, enlazado a los campos señalados (04-sistema-visual-y-accesibilidad.md).
 * Recibe el foco al aparecer para que lectores de pantalla lo anuncien de inmediato.
 */
export function ErrorSummary({ error, onRetry }: ErrorSummaryProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, [error]);

  return (
    <section className="error-summary" aria-labelledby="error-summary-heading" role="alert">
      <h3 id="error-summary-heading" tabIndex={-1} ref={headingRef}>
        Error <span className="mono">{error.code}</span>
      </h3>
      <p>{ERROR_COPY[error.code]}</p>
      {error.details.length > 0 && (
        <ul className="error-summary__details">
          {error.details.map((detail) => (
            <li key={`${detail.path}-${detail.message}`}>
              <span className="mono">{detail.path}</span>: {detail.message}
            </li>
          ))}
        </ul>
      )}
      <p className="error-summary__correlation">
        ID de correlación: <span className="mono">{error.correlationId}</span> (sin datos del
        payload)
      </p>
      {error.recoverable && onRetry && (
        <button type="button" className="btn btn--primary" onClick={onRetry}>
          Corregir y reintentar
        </button>
      )}
    </section>
  );
}
